import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Input, Select, DatePicker, Typography, Divider, List, message, Tabs, Row, Col, Upload, Popconfirm } from 'antd';
import { PlusOutlined, CalendarOutlined, DownloadOutlined, EditOutlined, UploadOutlined, FilePdfOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';
import ContatoSelect from '../components/ContatoSelect';
import { usePermissao } from '../hooks/usePermissao';
import CalendarioSimples from '../components/CalendarioSimples';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_COR = { aberta: 'blue', em_andamento: 'orange', pausada: 'gold', concluida: 'green', cancelada: 'red' };
const STATUS_LABEL = { aberta: 'Aberta', em_andamento: 'Em andamento', pausada: 'Pausada', concluida: 'Concluída', cancelada: 'Cancelada' };

const TIPO_COR = {
  os: '#1890ff', reuniao: '#722ed1', visita_tecnica: '#fa8c16',
  visita_comercial: '#52c41a', aniversario: '#eb2f96', comemorativo: '#faad14',
  feriado: '#ff4d4f', outro: '#8c8c8c'
};
const TIPO_LABEL = {
  os: 'OS', reuniao: 'Reunião', visita_tecnica: 'Visita Técnica',
  visita_comercial: 'Visita Comercial', aniversario: 'Aniversário',
  comemorativo: 'Data Comemorativa', feriado: 'Feriado', outro: 'Outro'
};

export default function Agendamentos() {
  const { podeExcluir, perfil } = usePermissao();
  const [osList, setOsList] = useState([]);
  const [compromissos, setCompromissos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [enderecos, setEnderecos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cnpjsTodos, setCnpjsTodos] = useState([]);
  useEffect(() => { api.get('/clientes/cnpjs/todos').then((r) => setCnpjsTodos(r.data || [])).catch(() => {}); }, []);
  const rotuloCnpj = (id) => { const c = cnpjsTodos.find((x) => x.id === id); return c ? c.cnpj + ' — ' + c.razao_social : ''; };
  const [eventos, setEventos] = useState([]);
  const [modalOS, setModalOS] = useState(false);
  const [modalDetalhe, setModalDetalhe] = useState(false);
  const [modalCompromisso, setModalCompromisso] = useState(false);
  const [modalDia, setModalDia] = useState(false);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [eventosDia, setEventosDia] = useState([]);
  const [osDetalhe, setOsDetalhe] = useState(null);
  const [modalLaudo, setModalLaudo] = useState(false);
  const [tipoLaudo, setTipoLaudo] = useState(null);
  const [subtipoLaudo, setSubtipoLaudo] = useState(null);
  const [subsubtipoLaudo, setSubsubtipoLaudo] = useState(null);
  const [compromissoEditando, setCompromissoEditando] = useState(null);
  const [novaAnotacao, setNovaAnotacao] = useState('');
  const [editandoOS, setEditandoOS] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroStatusOS, setFiltroStatusOS] = useState('');
  const [filtroOrdemOS, setFiltroOrdemOS] = useState('asc');
  const [filtroTipoComp, setFiltroTipoComp] = useState('');
  const [filtroOrdemComp, setFiltroOrdemComp] = useState('asc');
  const [form] = Form.useForm();
  const [formComp] = Form.useForm();
  const [formDetalhe] = Form.useForm();

  const carregarDados = useCallback(async () => {
    const [rOS, rComp, rCli, rUsu] = await Promise.all([
      api.get('/os'),
      api.get('/compromissos'),
      api.get('/clientes'),
      api.get('/usuarios'),
    ]);
    const os = rOS.data || [];
    const comp = rComp.data || [];
    setOsList(os);
    setCompromissos(comp);
    setClientes(rCli.data || []);
    setUsuarios(rUsu.data || []);

    // Montar eventos do calendário
    const evOS = os.filter(o => o.data_agendada).map(o => ({
      id: 'os_' + o.id,
      title: o.numero + ' — ' + (o.razao_social || o.nome_cliente || ''),
      start: o.data_agendada,
      color: TIPO_COR.os,
      extendedProps: { tipo: 'os', dados: o }
    }));
    const anoAtual = new Date().getFullYear();
    const evComp = [];
    comp.forEach(c => {
      const base = { id: 'comp_' + c.id, title: c.titulo, allDay: c.dia_todo, color: c.cor || TIPO_COR[c.tipo] || TIPO_COR.outro, extendedProps: { tipo: c.tipo, dados: c } };
      if (c.recorrencia === 'anual') {
        // Adicionar para ano atual e proximo
        for (let a = anoAtual - 1; a <= anoAtual + 1; a++) {
          const dataBase = new Date(c.data_inicio);
          dataBase.setFullYear(a);
          evComp.push({ ...base, id: base.id + '_' + a, start: dataBase.toISOString(), end: c.data_fim ? new Date(new Date(c.data_fim).setFullYear(a)).toISOString() : undefined });
        }
      } else if (c.recorrencia === 'mensal') {
        for (let m = 0; m < 12; m++) {
          const dataBase = new Date(c.data_inicio);
          dataBase.setFullYear(anoAtual);
          dataBase.setMonth(m);
          evComp.push({ ...base, id: base.id + '_m' + m, start: dataBase.toISOString() });
        }
      } else {
        evComp.push({ ...base, start: c.data_inicio, end: c.data_fim });
      }
    });

    setEventos([...evOS, ...evComp]);
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const carregarEnderecos = async (clienteId) => {
    if (!clienteId) { setEnderecos([]); return; }
    try {
      const [rEnd, rCnpjs] = await Promise.all([
        api.get('/clientes/' + clienteId + '/enderecos'),
        api.get('/clientes/' + clienteId + '/cnpjs'),
      ]);
      const ends = rEnd.data || [];
      const cnpjEnds = (rCnpjs.data || []).map(cn => ({
        id: 'cnpj_' + cn.id,
        nome_local: cn.razao_social + ' (Fiscal)',
        logradouro: cn.logradouro, numero: cn.numero,
        bairro: cn.bairro, cidade: cn.cidade, estado: cn.estado,
      }));
      setEnderecos([...ends, ...cnpjEnds]);
    } catch { setEnderecos([]); }
  };

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const osId = params.get('os');
    if (osId) {
      api.get('/os/' + osId).then(r => {
        setOsDetalhe(r.data);
        formDetalhe.setFieldsValue(r.data);
        setModalDetalhe(true);
      }).catch(() => {});
    }
  }, [location.search]);

  const abrirDetalhe = async (os) => {
    const r = await api.get('/os/' + os.id);
    setOsDetalhe(r.data);
    setEditandoOS(false);
    setModalDetalhe(true);
  };

  const salvarAnotacao = async () => {
    if (!novaAnotacao.trim()) return;
    await api.post('/os/' + osDetalhe.id + '/historico', { texto: novaAnotacao });
    setNovaAnotacao('');
    const r = await api.get('/os/' + osDetalhe.id);
    setOsDetalhe(r.data);
  };

  const atualizarStatus = async (status) => {
    await api.put('/os/' + osDetalhe.id, { ...osDetalhe, status });
    setOsDetalhe({ ...osDetalhe, status });
    carregarDados();
    message.success('Status atualizado!');
  };

  const exportarICS = async (os) => {
    try {
      const r = await api.get('/os/' + os.id + '/ics', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'os-' + os.id + '.ics'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Erro ao exportar ICS:', e.message); }
  };

  const excluirOS = async (id) => {
    try {
      await api.delete('/os/' + id);
      carregarDados();
      message.success('OS excluída!');
    } catch { message.error('Erro ao excluir OS'); }
  };

  const handleDateClick = (info) => {
    const data = info.dateStr;
    const evsDia = eventos.filter(e => {
      const start = e.start?.substring(0, 10);
      return start === data;
    });
    setDiaSelecionado(data);
    setEventosDia(evsDia);
    setModalDia(true);
  };

  const handleEventClick = (info) => {
    const { tipo, dados } = info.event.extendedProps;
    if (tipo === 'os') abrirDetalhe(dados);
  };

  const colunasOS = [
    { title: 'Ações', width: 130, render: (_, r) => (
      <Space size={2}>
        <Button size="small" icon={<EditOutlined />} onClick={() => abrirDetalhe(r)} />
        <Button size="small" type="primary" icon={<FileTextOutlined />} title="Criar Laudo" onClick={() => { setOsDetalhe(r); setTipoLaudo(null); setSubtipoLaudo(null); setSubsubtipoLaudo(null); setModalLaudo(true); }} />
        <Button size="small" icon={<CalendarOutlined />} onClick={() => exportarICS(r)} />
        {podeExcluir && <Popconfirm title="Excluir OS?" onConfirm={() => excluirOS(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
      </Space>
    )},
    { title: 'Número', dataIndex: 'numero', width: 130, render: (v) => <Text strong style={{ color: '#1a3c5e' }}>{v}</Text> },
    { title: 'Cliente', dataIndex: 'razao_social', render: (v, r) => v || r.nome_cliente },
    { title: 'Data Agendada', dataIndex: 'data_agendada', width: 150, render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-' },
    { title: 'Técnico', dataIndex: 'tecnico', width: 120 },
    { title: 'Status', dataIndex: 'status', width: 120, render: (v) => <Tag color={STATUS_COR[v]}>{STATUS_LABEL[v] || v}</Tag> },
  ];

  const colunasComp = [
    { title: 'Ações', width: 80, render: (_, r) => (
      <Space size={2}>
        {(perfil === 'admin' || r.tipo !== 'aniversario') && <Button size="small" icon={<EditOutlined />} onClick={() => { setCompromissoEditando(r); formComp.setFieldsValue({ ...r, data_inicio: dayjs(r.data_inicio), data_fim: r.data_fim ? dayjs(r.data_fim) : null }); setModalCompromisso(true); }} />}
        {(perfil === 'admin' || r.tipo !== 'aniversario') && <Popconfirm title="Remover?" onConfirm={async () => { await api.delete('/compromissos/' + r.id); carregarDados(); }}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
      </Space>
    )},
    { title: 'Tipo', dataIndex: 'tipo', width: 130, render: (v) => <Tag color={TIPO_COR[v]}>{TIPO_LABEL[v] || v}</Tag> },
    { title: 'Título', dataIndex: 'titulo' },
    { title: 'Data', dataIndex: 'data_inicio', width: 150, render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '-' },
    { title: 'Público', dataIndex: 'publico', width: 80, render: (v) => v ? <Tag color="green">Sim</Tag> : <Tag>Não</Tag> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Agendamentos</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOS(true); }}>Nova OS</Button>
          <Button icon={<CalendarOutlined />} onClick={() => { setCompromissoEditando(null); formComp.resetFields(); setModalCompromisso(true); }}>Novo Compromisso</Button>
        </Space>
      </div>

      {/* Calendário simples */}
      <Card style={{ marginBottom: 16 }}>
        <CalendarioSimples eventos={eventos} onDiaClick={handleDateClick} onEventoClick={handleEventClick} />
      </Card>

      {/* Abas */}
      <Tabs defaultActiveKey="os">
        <Tabs.TabPane tab={`Ordens de Serviço (${osList.length})`} key="os">
          <Card>
            <Space wrap style={{ marginBottom: 12 }}>
              <Input.Search placeholder="Buscar por número, cliente ou técnico..." allowClear onChange={e => setBusca(e.target.value.toLowerCase())} style={{ width: 280 }} />
              <Select allowClear placeholder="Filtrar status" onChange={v => setFiltroStatusOS(v||'')} style={{ width: 150 }}>
                {Object.entries(STATUS_LABEL).map(([k,v]) => <Option key={k} value={k}>{v}</Option>)}
              </Select>
              <Select value={filtroOrdemOS} onChange={v => setFiltroOrdemOS(v)} style={{ width: 150 }}>
                <Option value="asc">Data ↑ (mais antiga)</Option>
                <Option value="desc">Data ↓ (mais recente)</Option>
              </Select>
            </Space>
            <Table dataSource={osList
              .filter(os => (!busca || (os.numero||'').toLowerCase().includes(busca) || (os.razao_social||os.nome_cliente||'').toLowerCase().includes(busca) || (os.tecnico||'').toLowerCase().includes(busca)) && (!filtroStatusOS || os.status === filtroStatusOS))
              .sort((a, b) => { const da = new Date(a.data_agendada||0); const db = new Date(b.data_agendada||0); return filtroOrdemOS === 'asc' ? da-db : db-da; })}
              columns={colunasOS} rowKey="id" size="small" scroll={{ x: 800 }} />
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`Compromissos (${compromissos.filter(c => c.tipo !== 'feriado').length})`} key="compromissos">
          <Card>
            <Space wrap style={{ marginBottom: 12 }}>
              <Select allowClear placeholder="Filtrar tipo" onChange={v => setFiltroTipoComp(v||'')} style={{ width: 180 }}>
                {Object.entries(TIPO_LABEL).filter(([k]) => k !== 'os' && k !== 'feriado').map(([k,v]) => <Option key={k} value={k}>{v}</Option>)}
              </Select>
              <Select value={filtroOrdemComp} onChange={v => setFiltroOrdemComp(v)} style={{ width: 150 }}>
                <Option value="asc">Data ↑ (mais antiga)</Option>
                <Option value="desc">Data ↓ (mais recente)</Option>
              </Select>
            </Space>
            <Table dataSource={compromissos
              .filter(c => c.tipo !== 'feriado' && (!filtroTipoComp || c.tipo === filtroTipoComp))
              .sort((a, b) => { const da = new Date(a.data_inicio||0); const db = new Date(b.data_inicio||0); return filtroOrdemComp === 'asc' ? da-db : db-da; })}
              columns={colunasComp} rowKey="id" size="small" scroll={{ x: 700 }} />
          </Card>
        </Tabs.TabPane>
      </Tabs>

      {/* Modal Dia */}
      <Modal title={'Eventos de ' + (diaSelecionado ? dayjs(diaSelecionado).format('DD/MM/YYYY') : '')} open={modalDia} onCancel={() => setModalDia(false)} footer={null}>
        {eventosDia.length === 0 ? <Text type="secondary">Nenhum evento neste dia.</Text> : (
          <List dataSource={eventosDia} renderItem={ev => (
            <List.Item>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: ev.color }} />
                <Text>{ev.title}</Text>
              </div>
            </List.Item>
          )} />
        )}
      </Modal>

      {/* Modal Nova OS */}
      <Modal title="Nova Ordem de Serviço" open={modalOS} onCancel={() => setModalOS(false)} footer={null} width={600}>
        <Form form={form} layout="vertical" onFinish={async (values) => {
          try {
            await api.post('/os', { ...values, data_agendada: values.data_agendada?.toISOString() });
            message.success('OS criada!');
            setModalOS(false);
            carregarDados();
          } catch { message.error('Erro ao criar OS'); }
        }}>
          <Form.Item name="cliente_id" label="Cliente" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Buscar por nome ou CNPJ"
              onChange={(val) => { carregarEnderecos(val); form.setFieldsValue({ endereco_atendimento: '' }); }}
              filterOption={(input, option) => {
                const cl = clientes.find(c => c.id === option.value);
                return (cl?.razao_social || cl?.nome || '').toLowerCase().includes(input.toLowerCase()) ||
                       (cl?.cnpj || '').replace(/\D/g,'').includes(input.replace(/\D/g,''));
              }}>
              {clientes.map(c => <Option key={c.id} value={c.id}>{c.razao_social || c.nome} {c.cnpj ? '— ' + c.cnpj : ''}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="endereco_id_sel" label="Endereço de Atendimento">
            <Select allowClear placeholder="Selecione um endereço cadastrado"
              onChange={(val) => {
                const end = enderecos.find(e => String(e.id) === String(val));
                if (end) {
                  const txt = [end.logradouro, end.numero, end.bairro, end.cidade, end.estado].filter(Boolean).join(', ');
                  form.setFieldsValue({ endereco_atendimento: txt });
                }
              }}>
              {enderecos.map(e => <Option key={e.id} value={String(e.id)}>{e.nome_local} — {e.logradouro}, {e.numero}, {e.cidade}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="endereco_atendimento" label="Endereço Completo" rules={[{ required: true }]}>
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="data_agendada" label="Data e Hora Agendada">
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>{() => (<Form.Item name="contato_id" label="Contato"><ContatoSelect clienteId={form.getFieldValue('cliente_id')} /></Form.Item>)}</Form.Item>
          <Form.Item name="tecnico" label="Técnico Responsável">
            <Select allowClear showSearch placeholder="Selecione o técnico" optionFilterProp="children">
              {usuarios.filter(u => u.ativo).map(u => <Option key={u.id} value={u.nome}>{u.nome} {u.crea ? '— CREA: ' + u.crea : ''}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="atividade" label="Atividade">
            <TextArea rows={3} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setModalOS(false)}>Cancelar</Button>
            <Button type="primary" htmlType="submit">Criar OS</Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Compromisso */}
      <Modal title={compromissoEditando ? 'Editar Compromisso' : 'Novo Compromisso'} open={modalCompromisso} onCancel={() => setModalCompromisso(false)} footer={null} width={500}>
        <Form form={formComp} layout="vertical" onFinish={async (values) => {
          try {
            const dados = { ...values, data_inicio: values.data_inicio?.toISOString(), data_fim: values.data_fim?.toISOString() };
            if (compromissoEditando) await api.put('/compromissos/' + compromissoEditando.id, dados);
            else await api.post('/compromissos', dados);
            message.success('Salvo!');
            setModalCompromisso(false);
            carregarDados();
          } catch { message.error('Erro ao salvar'); }
        }}>
          <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
            <Select>
              {Object.entries(TIPO_LABEL).filter(([k]) => k !== 'os' && (k !== 'aniversario' || perfil === 'admin')).map(([k, v]) => <Option key={k} value={k}><span style={{ color: TIPO_COR[k] }}>●</span> {v}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="titulo" label="Título" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="descricao" label="Descrição"><TextArea rows={2} /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="data_inicio" label="Data/Hora Início" rules={[{ required: true }]}><DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="data_fim" label="Data/Hora Fim"><DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="recorrencia" label="Repetição" initialValue="nenhuma">
            <Select>
              <Option value="nenhuma">Não repete</Option>
              <Option value="semanal">Toda semana</Option>
              <Option value="mensal">Todo mês</Option>
              <Option value="anual">Todo ano (aniversários, feriados)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="publico" label="Visível para todos?" initialValue={true}>
            <Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select>
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setModalCompromisso(false)}>Cancelar</Button>
            <Button type="primary" htmlType="submit">Salvar</Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Detalhe OS */}
      <Modal title={osDetalhe?.numero} open={modalDetalhe} onCancel={() => setModalDetalhe(false)} footer={null} width={700}>
        {osDetalhe && (
          <div>
            <Card size="small" style={{ marginBottom: 12 }} extra={
              <Space size={4}>
                <Button size="small" type="primary" icon={<FileTextOutlined />} onClick={() => { setTipoLaudo(null); setSubtipoLaudo(null); setSubsubtipoLaudo(null); setModalLaudo(true); }}>Criar Laudo</Button>
                <Button size="small" icon={<EditOutlined />} onClick={() => { setEditandoOS(true); formDetalhe.setFieldsValue({ ...osDetalhe, data_agendada: osDetalhe.data_agendada ? dayjs(osDetalhe.data_agendada) : null }); }}>Editar</Button>
              </Space>
            }>
              {editandoOS ? (
                <Form form={formDetalhe} layout="vertical" onFinish={async (values) => {
                  await api.put('/os/' + osDetalhe.id, { ...osDetalhe, ...values, data_agendada: values.data_agendada?.toISOString() });
                  const r = await api.get('/os/' + osDetalhe.id);
                  setOsDetalhe(r.data);
                  setEditandoOS(false);
                  carregarDados();
                  message.success('OS atualizada!');
                }}>
                  <Row gutter={12}>
                    <Col span={12}><Form.Item name="data_agendada" label="Data e Hora"><DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="contato_id" label="Contato"><ContatoSelect clienteId={osDetalhe?.cliente_id} /></Form.Item></Col><Col span={12}><Form.Item name="tecnico" label="Técnico"><Select showSearch optionFilterProp="children">{usuarios.filter(u => u.ativo).map(u => <Option key={u.id} value={u.nome}>{u.nome} {u.crea ? "— CREA: " + u.crea : ""}</Option>)}</Select></Form.Item></Col>
                  </Row>
                  <Form.Item name="endereco_atendimento" label="Endereço"><TextArea rows={2} /></Form.Item>
                  <Form.Item name="cnpj_laudo_id" label="CNPJ do laudo" extra="Vai em todos os laudos desta OS (razão social, CNPJ e ART). Padrão: o CNPJ de faturamento.">
                    <Select showSearch allowClear optionFilterProp="children" placeholder="Pesquise pelo CNPJ">
                      {cnpjsTodos.map((c) => <Option key={c.id} value={c.id}>{c.cnpj} — {c.razao_social} {c.cliente_nome ? '(' + c.cliente_nome + ')' : ''}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="atividade" label="Atividade"><TextArea rows={2} /></Form.Item>
                  <Space><Button onClick={() => setEditandoOS(false)}>Cancelar</Button><Button type="primary" htmlType="submit">Salvar</Button></Space>
                </Form>
              ) : (
                <>
                  <Space wrap>
                    <Text><strong>Cliente:</strong> {osDetalhe.razao_social || osDetalhe.nome_cliente}</Text>
                    {osDetalhe.cnpj_filial && <Text><br/><strong>CNPJ Faturamento:</strong> {osDetalhe.cnpj_filial} — {osDetalhe.razao_filial}</Text>}
                    {(osDetalhe.cnpj_laudo_id || osDetalhe.cnpj_faturamento_id) && <Text><br/><strong>CNPJ do laudo:</strong> {rotuloCnpj(osDetalhe.cnpj_laudo_id || osDetalhe.cnpj_faturamento_id) || '-'}</Text>}
                    <Text><strong>Técnico:</strong> {osDetalhe.tecnico || '-'}</Text>
                    <Text><strong>Data:</strong> {osDetalhe.data_agendada ? dayjs(osDetalhe.data_agendada).format('DD/MM/YYYY HH:mm') : '-'}</Text>
                    <Text><strong>Status:</strong> <Tag color={STATUS_COR[osDetalhe.status]}>{STATUS_LABEL[osDetalhe.status]}</Tag></Text>
                  </Space>
                  {osDetalhe.endereco_atendimento && <div style={{ marginTop: 8 }}><Text><strong>Endereço:</strong> {osDetalhe.endereco_atendimento}</Text></div>}
                  {osDetalhe.atividade && <div style={{ marginTop: 8 }}><Text><strong>Atividade:</strong> {osDetalhe.atividade}</Text></div>}
                </>
              )}
            </Card>

            <Divider orientation="left">Atualizar Status</Divider>
            <Space wrap style={{ marginBottom: 12 }}>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <Button key={k} size="small" type={osDetalhe.status === k ? 'primary' : 'default'} onClick={() => atualizarStatus(k)}>{v}</Button>
              ))}
            </Space>

            <Divider orientation="left">Histórico</Divider>
            <List size="small" dataSource={osDetalhe.historico || []} style={{ marginBottom: 12 }}
              locale={{ emptyText: 'Nenhuma anotação ainda.' }}
              renderItem={h => (
                <List.Item>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(h.criado_em).format('DD/MM/YYYY HH:mm')} — {h.usuario}</Text>
                    <div>{h.texto}</div>
                  </div>
                </List.Item>
              )} />
            <Space.Compact style={{ width: '100%' }}>
              <TextArea rows={2} value={novaAnotacao} onChange={e => setNovaAnotacao(e.target.value)} placeholder="Nova anotação..." />
            </Space.Compact>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <Space>
                <Button icon={<DownloadOutlined />} onClick={() => exportarICS(osDetalhe)}>Exportar Calendário</Button>
                <Upload accept=".pdf,.jpg,.jpeg,.png,.webp" showUploadList={false} beforeUpload={async (file) => {
                  const fd = new FormData();
                  fd.append('arquivo', file);
                  try {
                    await api.post('/os/' + osDetalhe.id + '/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    message.success('Arquivo enviado!');
                    const r = await api.get('/os/' + osDetalhe.id);
                    setOsDetalhe(r.data);
                  } catch { message.error('Erro ao enviar'); }
                  return false;
                }}>
                  <Button icon={<UploadOutlined />}>Upload OS/RDO</Button>
                </Upload>
                {osDetalhe.arquivo_caminho && (
                  <Button icon={<FilePdfOutlined />} onClick={async () => {
                    const r = await api.get('/os/' + osDetalhe.id + '/download', { responseType: 'blob' });
                    const contentType = r.headers['content-type'] || 'application/pdf';
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: contentType }));
                    window.open(url, '_blank');
                  }}>Ver OS/RDO</Button>
                )}
              </Space>
              <Button type="primary" onClick={salvarAnotacao}>Adicionar Anotação</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Criar Laudo */}
      <Modal title="Criar Laudo" open={modalLaudo} onCancel={() => setModalLaudo(false)} footer={null} width={500}>
        <Form layout="vertical">
          <Form.Item label="Tipo de Laudo" required>
            <Select placeholder="Selecione o tipo" value={tipoLaudo} onChange={v => { setTipoLaudo(v); setSubtipoLaudo(null); setSubsubtipoLaudo(null); }} style={{ width: '100%' }}>
              <Option value="inspecao">Inspeção NR-13</Option>
              <Option value="calibracao">Calibração</Option>
              <Option value="estanqueidade">Estanqueidade</Option>
              <Option value="end">END</Option>
              <Option value="manutencao">Manutenção</Option>
              <Option value="diversos">Diversos</Option>
            </Select>
          </Form.Item>
          {tipoLaudo && (
            <Form.Item label="Subtipo" required>
              <Select placeholder="Selecione o subtipo" value={subtipoLaudo} onChange={v => { setSubtipoLaudo(v); setSubsubtipoLaudo(null); }} style={{ width: '100%' }}>
                {tipoLaudo === 'inspecao' && [<Option key="vaso" value="vaso">Vaso de Pressão</Option>, <Option key="tanque" value="tanque">Tanque</Option>, <Option key="tubulacao" value="tubulacao">Tubulação</Option>, <Option key="autoclave" value="autoclave">Autoclave</Option>, <Option key="caldeira" value="caldeira">Caldeira</Option>]}
                {tipoLaudo === 'calibracao' && [<Option key="manometro" value="manometro">Manômetro / Manovacuômetro / Transdutor de Pressão</Option>, <Option key="valvula" value="valvula">Válvula de Segurança</Option>, <Option key="pressostato" value="pressostato">Pressostato</Option>, <Option key="termometro" value="termometro">Termômetro / Termorresistência</Option>, <Option key="calibrador" value="calibrador">Calibrador de Pneu</Option>, <Option key="incubadora" value="incubadora">Incubadora</Option>, ]}
                {tipoLaudo === 'estanqueidade' && [<Option key="hidrostatico" value="hidrostatico">Teste Hidrostático</Option>, <Option key="tubulacao" value="tubulacao">Estanqueidade (bolha) — Tubulação</Option>, <Option key="tanque" value="tanque">Estanqueidade (bolha) — Tanque</Option>, <Option key="vaso" value="vaso">Estanqueidade (bolha) — Vaso de Pressão</Option>, <Option key="outro" value="outro">Estanqueidade (bolha) — Outro equipamento</Option>]}
                {tipoLaudo === 'end' && [<Option key="liquido" value="liquido">Líquido Penetrante</Option>, <Option key="espessura" value="espessura">Medição de Espessura</Option>]}
                {tipoLaudo === 'manutencao' && <Option key="autoclave" value="autoclave">Autoclave</Option>}
                {tipoLaudo === 'diversos' && <Option key="avulso" value="avulso">Laudo avulso (editor livre)</Option>}
                {tipoLaudo === 'diversos' && <Option key="outro" value="outro">Outro</Option>}
              </Select>
            </Form.Item>
          )}
          {tipoLaudo === 'inspecao' && subtipoLaudo && (
            <Form.Item label="Tipo de Inspeção" required>
              <Select placeholder="Selecione" value={subsubtipoLaudo} onChange={setSubsubtipoLaudo} style={{ width: '100%' }}>
                {subtipoLaudo === 'vaso' && [<Option key="ie" value="Interna e Externa">Interna e Externa</Option>, <Option key="i" value="Interna">Interna</Option>, <Option key="e" value="Externa">Externa</Option>, <Option key="cie" value="Criogenia - Interna e Externa">Criogenia - Interna e Externa</Option>, <Option key="ci" value="Criogenia - Interna">Criogenia - Interna</Option>, <Option key="ce" value="Criogenia - Externa">Criogenia - Externa</Option>]}
                {subtipoLaudo === 'tanque' && [<Option key="g" value="Geral">Geral</Option>]}
                {subtipoLaudo === 'tubulacao' && [<Option key="ie" value="Interna e Externa">Interna e Externa</Option>]}
                {subtipoLaudo === 'autoclave' && [<Option key="sie" value="Simples - Interna e Externa">Simples - Interna e Externa</Option>, <Option key="si" value="Simples - Interna">Simples - Interna</Option>, <Option key="se" value="Simples - Externa">Simples - Externa</Option>, <Option key="gvie" value="Gerador de Vapor - Interna e Externa">Gerador de Vapor - Interna e Externa</Option>, <Option key="gvi" value="Gerador de Vapor - Interna">Gerador de Vapor - Interna</Option>, <Option key="gve" value="Gerador de Vapor - Externa">Gerador de Vapor - Externa</Option>, <Option key="csie" value="Câmara de Esterilização - Interna e Externa">Câmara de Esterilização - Interna e Externa</Option>, <Option key="csi" value="Câmara de Esterilização - Interna">Câmara de Esterilização - Interna</Option>, <Option key="cse" value="Câmara de Esterilização - Externa">Câmara de Esterilização - Externa</Option>]}
                {subtipoLaudo === 'caldeira' && [<Option key="ie" value="Interna e Externa">Interna e Externa</Option>, <Option key="i" value="Interna">Interna</Option>, <Option key="e" value="Externa">Externa</Option>]}
              </Select>
            </Form.Item>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button onClick={() => setModalLaudo(false)}>Cancelar</Button>
            <Button type="primary"
              disabled={!tipoLaudo || !subtipoLaudo || (tipoLaudo === 'inspecao' && !subsubtipoLaudo)}
              onClick={async () => {
                if (tipoLaudo === 'calibracao') {
                  const mapa = { manometro: 'manometro', valvula: 'valvula', termometro: 'termometro', calibrador: 'calibrador_pneu', pressostato: 'pressostato', incubadora: 'incubadora' };
                  const tc = mapa[subtipoLaudo];
                  if (!tc) { message.warning('Esse tipo de calibracao ainda nao esta disponivel.'); return; }
                  try {
                    const rc = await api.post('/calibracoes/da-os/' + osDetalhe.id, { tipo: tc });
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/calibracoes/' + rc.data.id;
                  } catch (e) { message.error('Erro ao criar calibracao'); }
                  return;
                }
                if (tipoLaudo === 'diversos') {
                  if (subtipoLaudo !== 'avulso') { message.warning('Esse laudo ainda nao esta disponivel.'); return; }
                  try {
                    const rav = await api.post('/laudos-avulsos/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/laudos-avulsos/' + rav.data.id;
                  } catch (e) { message.error('Erro ao criar o laudo avulso'); }
                  return;
                }
                if (tipoLaudo === 'manutencao') {
                  if (subtipoLaudo !== 'autoclave') { message.warning('Esse laudo ainda nao esta disponivel.'); return; }
                  try {
                    const rm = await api.post('/manutencao-autoclave/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/manutencao-autoclave/' + rm.data.id;
                  } catch (e) { message.error('Erro ao criar laudo'); }
                  return;
                }
                // Medicao de espessura tem laudo proprio e serve a qualquer
                // equipamento: o tipo e escolhido dentro do formulario.
                if (tipoLaudo === 'estanqueidade' && subtipoLaudo !== 'hidrostatico') {
                  const EQ_EST = { tubulacao: 'Tubulação', tanque: 'Tanque', vaso: 'Vaso de Pressão', outro: 'Outro' };
                  try {
                    const rest = await api.post('/testes-estanqueidade/da-os/' + osDetalhe.id, { tipo_equipamento: EQ_EST[subtipoLaudo] || 'Outro' });
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/testes-estanqueidade/' + rest.data.id;
                  } catch (e) { message.error('Erro ao criar o laudo de estanqueidade'); }
                  return;
                }
                if (tipoLaudo === 'estanqueidade' && subtipoLaudo === 'hidrostatico') {
                  try {
                    const rth = await api.post('/testes-hidrostaticos/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/testes-hidrostaticos/' + rth.data.id;
                  } catch (e) { message.error('Erro ao criar o laudo de teste hidrostático'); }
                  return;
                }
                if (tipoLaudo === 'end' && subtipoLaudo === 'espessura') {
                  try {
                    const rme = await api.post('/inspecoes-espessura/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/inspecoes-espessura/' + rme.data.id;
                  } catch (e) { message.error('Erro ao criar o relatório de medição de espessura'); }
                  return;
                }
                if (tipoLaudo === 'end') {
                  if (subtipoLaudo !== 'liquido') { message.warning('Esse ensaio ainda nao esta disponivel.'); return; }
                  try {
                    const rl = await api.post('/ensaios-lp/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/ensaios-lp/' + rl.data.id;
                  } catch (e) { message.error('Erro ao criar ensaio'); }
                  return;
                }
                // Tanque tem laudo proprio: a NR-13 trata no item 13.7 e a
                // inspecao e sempre geral, sem divisao em interna e externa.
                // O proprio laudo decide se cita a NR-13, pelo enquadramento
                // do item 13.2.1, alinea (f).
                if (tipoLaudo === 'inspecao' && subtipoLaudo === 'tanque') {
                  try {
                    const rq = await api.post('/inspecoes-tanque/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/inspecoes-tanque/' + rq.data.id;
                  } catch (e) { message.error('Erro ao criar o laudo de tanque'); }
                  return;
                }
                // Tubulacao tem laudo proprio: a NR-13 trata no item 13.6 e a
                // inspecao nao se divide em interna e externa. Vem antes da regra
                // da externa, senao "Tubulacao - Externa" cairia no modulo errado.
                if (tipoLaudo === 'inspecao' && subtipoLaudo === 'tubulacao') {
                  try {
                    const rt = await api.post('/inspecoes-tubulacao/da-os/' + osDetalhe.id, {});
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/inspecoes-tubulacao/' + rt.data.id;
                  } catch (e) { message.error('Erro ao criar o laudo de tubulação'); }
                  return;
                }
                // Externa pura e um laudo proprio. "Interna e Externa" nao
                // entra aqui: so o que termina exatamente em "Externa".
                const partes = String(subsubtipoLaudo || '').split(' - ').map((x) => x.trim());
                const soExterna = partes[partes.length - 1] === 'Externa';
                if (tipoLaudo === 'inspecao' && soExterna) {
                  const prefixo = partes.length > 1 ? partes[0] : '';
                  // Criogenia, tanque, tubulacao e caldeira terao laudo proprio
                  const equip = subtipoLaudo === 'vaso' && !prefixo ? 'Vaso de Pressão'
                    : subtipoLaudo === 'autoclave' && prefixo.indexOf('Gerador') >= 0 ? 'Gerador de Vapor'
                    : subtipoLaudo === 'autoclave' && prefixo.indexOf('mara') >= 0 ? 'Câmara de Esterilização'
                    : subtipoLaudo === 'autoclave' ? 'Autoclave'
                    : null;
                  if (!equip) { message.warning('Esse laudo de inspeção externa ainda não está disponível.'); return; }
                  try {
                    const re = await api.post('/inspecoes-externas/da-os/' + osDetalhe.id, { tipo_equipamento: equip });
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/inspecoes-externas/' + re.data.id;
                  } catch (e) { message.error('Erro ao criar inspeção externa'); }
                  return;
                }
                // Camara de esterilizacao tem laudo proprio: a geometria e
                // retangular (Apendice 13 do ASME VIII), e ela tem porta de
                // fechamento rapido (UG-35.2) e jaqueta, que o laudo de vaso
                // nao cobre. A variante "Externa" pura ja foi tratada acima.
                if (tipoLaudo === 'inspecao' && subtipoLaudo === 'autoclave'
                    && partes[0] === 'C\u00e2mara de Esteriliza\u00e7\u00e3o') {
                  try {
                    const rcam = await api.post('/inspecoes-camara/da-os/' + osDetalhe.id, {
                      tipo_inspecao: partes[partes.length - 1] === 'Interna'
                        ? 'interna' : 'interna_externa'
                    });
                    setModalLaudo(false); setModalDetalhe(false);
                    window.location.href = '/inspecoes-camara/' + rcam.data.id;
                  } catch (e) { message.error('Erro ao criar laudo da c\u00e2mara'); }
                  return;
                }
                const params = new URLSearchParams({ os_id: osDetalhe.id, tipo: tipoLaudo, subtipo: subtipoLaudo, subsubtipo: subsubtipoLaudo || '', cliente_id: osDetalhe.cliente_id || '', proposta_id: osDetalhe.proposta_id || '' });
                setModalLaudo(false);
                setModalDetalhe(false);
                window.location.href = '/laudos/novo?' + params.toString();
              }}>Criar Laudo →</Button>
          </div>
        </Form>
      </Modal>

    </div>
  );
}
